/**
 * The one place that talks to the server.
 *
 * Auth is the session cookie the server already issues: httpOnly, SameSite,
 * and never readable from here. That is on purpose — a token in localStorage
 * would be readable by any script that got onto the page, and this product
 * shows contract exposure. Every write carries the CSRF token, which the
 * server derives from that cookie and hands back on /api/session.
 */

export class ApiError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(detail);
  }
}

let csrf = "";

export function setCsrf(token: string) {
  csrf = token;
}

async function parse(response: Response) {
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new ApiError(response.status, body?.detail ?? response.statusText);
  }
  return body;
}

export async function get<T>(path: string): Promise<T> {
  return parse(await fetch(`/api${path}`, { credentials: "same-origin" }));
}

export async function post<T>(path: string, payload: unknown = {}): Promise<T> {
  return parse(
    await fetch(`/api${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...(payload as object), csrf }),
    }),
  );
}

/**
 * A figure the server may have withheld.
 *
 * `null` means the record holds no figure. `{withheld: true}` means there is
 * one and this reader may not see it. Collapsing the two would report an
 * event with no exposure, which is the worse mistake — so the type keeps
 * them apart and <Money> renders them differently.
 */
export type Figure = number | null | { withheld: true };

export function isWithheld(value: Figure): value is { withheld: true } {
  return typeof value === "object" && value !== null && "withheld" in value;
}

// ---------------------------------------------------------------------------

export interface NavItem { label: string; href: string; group: string }

export interface Session {
  subject: string;
  display_name: string;
  roles: string[];
  permissions: string[];
  contracts: string[] | null;
  nav: NavItem[];
  csrf: string;
  product?: string;
}

export interface ContractRecord {
  id: string; reference: string; project: string;
  client: string; contractor: string;
  form: string; main_option: string; live_clocks: number;
}

export interface RadarRow {
  clock_id: string;
  reference: string;
  /** Which contract this belongs to, for a portfolio view. */
  contract: string;
  title: string;
  clock: string;
  clause: string;
  state: string;
  state_label: string;
  next_deadline: string | null;
  days: number | null;
  /** The clock's stations, so a surface can draw its shape rather than
   *  describing it in words: trigger, reply due, notice, outcome. */
  trigger_date: string | null;
  reply_due_date: string | null;
  reminder_notice_date: string | null;
  deemed_date: string | null;
  exposure_gbp: Figure;
  exposure_days: number | null;
  severity: number;
  severity_band: "critical" | "high" | "medium" | "low";
  pm_breach: boolean;
  responsible_party: string;
  outcome_if_ignored: string;
}

export interface RadarSummary {
  clocks_total: number; clocks_live: number; deemed: number;
  time_barred: number; pm_breaches: number; in_further_period: number;
  value_at_risk_gbp: Figure; value_lost_gbp: Figure; contracts: number;
}

export interface EventClock {
  id: string; clause: string; state: string;
  trigger_date: string | null;
  reply_period_days: number | null; further_period_days: number | null;
  reply_due_date: string | null;
  replied_date: string | null; reminder_notice_date: string | null;
  deemed_date: string | null; next_deadline_date: string | null;
  days_to_next_deadline: number | null; default_outcome: string;
  responsible_party: string; pm_breach: boolean; exposure_gbp: Figure;
}

export interface EventRecord {
  id: string; reference: string; event_type: string; title: string;
  description: string; raised_by: string;
  notified_date: string | null; awareness_date: string | null;
  instruction_date: string | null; status: string;
  value_gbp: Figure; time_impact_days: number | null;
}

export interface CorrespondenceRecord {
  id: string; sent_date: string | null; sender: string; subject: string;
  category: string; confidence: number | null;
  linked_event: string | null; link_confirmation: string;
  confirmed_by: string | null;
}

export interface Provenance {
  journal_ref: string; recorded_at: string | null; actor: string;
}

export interface Since {
  days: number; sentences: string[]; moves: number;
  irreversible: number; by_state: Record<string, number>;
}

export interface EventDetailPayload {
  event: EventRecord;
  clocks: EventClock[];
  correspondence: CorrespondenceRecord[];
  actions: ActionDetail[];
  /** Where in the journal each automatic outcome was recorded. */
  provenance?: Record<string, Provenance>;
}

export interface SourceFile {
  name: string; bytes: number; modified: string | null;
}

export interface SourceRecord {
  key: string; description: string; reading_from: string; path: string;
  accepts: string[]; files: SourceFile[];
}

export interface SourcesPayload {
  sources: SourceRecord[];
  last_read_at: string | null;
  poll_minutes: number;
  max_upload_bytes: number;
  may_supply: boolean;
  may_refresh: boolean;
}

export interface BoardRow {
  id: string; priority: number; title: string; owner: string;
  due_date: string | null; status: string; detail: string;
  has_draft: boolean; confirmation: string; clause: string; severity: number;
  /** The clock behind the item, so the board ranks as the radar does. */
  state: string | null; deemed_date: string | null;
}

/**
 * Words the server may have withheld. An unsent letter is its author's
 * side's position: the other party may know it is being prepared, and may
 * not read it until it is sent. Distinct from "" (no draft) for the same
 * reason a withheld figure is distinct from null.
 */
export type Words = string | { withheld: true };

export function isWithheldText(value: Words | null | undefined): value is { withheld: true } {
  return typeof value === "object" && value !== null && "withheld" in value;
}

/** The text, or null when there is none or it is not this reader's to see. */
export function wordsOf(value: Words | null | undefined): string | null {
  return typeof value === "string" && value ? value : null;
}

export interface ActionDetail {
  id: string; title: string; detail: string; owner: string;
  due_date: string | null; status: string; confirmation: string;
  draft_subject: Words; draft_body: Words;
  drafted_by: string | null; template: string | null;
  may_complete: boolean;
}

export interface ShadowRow {
  kind: string; reference: string; title: string; clause: string;
  register_says: string; contract_says: string; detail: string;
  severity_band: string;
}

export interface ImpactWindow {
  start: string; end: string; label: string;
  baseline_percent: number; impacted_percent: number;
}

export interface ImpactEvent {
  reference: string;
  title: string;
  /** Whether a fragnet has been supplied, so an assessment can run at all. */
  assessable?: boolean;
}

export interface ImpactResult {
  event: string; dividing_date: string; dividing_date_basis: string;
  clause: string; is_accepted_programme: boolean; baseline_basis: string;
  baseline_revision: string;
  baseline_planned_completion: string | null;
  impacted_planned_completion: string | null;
  delay_days: number; delay_working_days: number; on_critical_path: boolean;
  terminal_float_before: number; terminal_float_after: number;
  time_risk_allowance_consumed: { code: string; name: string }[];
  narrative: string; warnings: string[]; window: ImpactWindow | null;
}

/**
 * Send a file to a source folder.
 *
 * Multipart rather than JSON, so it carries the CSRF token as a form
 * field instead of in a body object -- the check on the server is the
 * same one, reading from the same place a form would put it.
 */
export async function upload(path: string, file: File): Promise<unknown> {
  const form = new FormData();
  form.append("file", file);
  form.append("csrf", csrf);
  return parse(await fetch(`/api${path}`, {
    method: "POST", credentials: "same-origin", body: form,
  }));
}
